import {ChangeDetectionStrategy, Component, OnInit, Input} from '@angular/core';
import {untilComponentDestroyed, OnDestroyMixin} from '@w11k/ngx-componentdestroyed';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, ReplaySubject, take} from 'rxjs';

import {UnitStatementReqRes} from '../../../models/gp/UnitStatementReqRes.model';
import {TerraUtils} from '../../../TerraUtils';
import {UnitBankAccountService} from '../unit-bank-account/unit-bank-account.service';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';
import {UserService} from 'src/app/services/shared/user.service';

@Component({
  selector: 'terra-unit-bank-account-statement',
  templateUrl: './unit-bank-account-statement.component.html',
  styleUrls: ['./unit-bank-account-statement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitBankAccountStatementComponent extends OnDestroyMixin implements OnInit {
  @Input() statements: UnitStatementReqRes[];
  @Input() unitBankAccountId: string;

  statementForm: UntypedFormGroup;
  availableYears$ = new ReplaySubject<string[]>(1);
  availableMonths$ = new ReplaySubject<string[]>(1);
  isDownloading$ = new BehaviorSubject<boolean>(false);
  errorDownloading$ = new BehaviorSubject<boolean>(false);

  constructor(private fb: UntypedFormBuilder,
              private unitBankAccountService: UnitBankAccountService,
              private telemetryService: TelemetryService,
              private userService: UserService) {
    super();
  }

  ngOnInit(): void {
    this.generateForm();
    this.subscribeToYearChange();
    this.setAvailableYears();
  }

  /**
   * Downloads monthly statement from the server
   */
  public getStatement() {
    this.statementForm.markAsTouched();
    this.statementForm.updateValueAndValidity();
    this.errorDownloading$.next(false);
    if (!this.statementForm.valid) {
      return;
    }

    this.isDownloading$.next(true);
    const selectedYear = this.statementForm.get('year').value;
    const selectedMonth = this.statementForm.get('month').value;
    const statementId = this.statements.filter(statement => statement.year === selectedYear && statement.month === selectedMonth)[0]?.statementId;
    this.unitBankAccountService.downloadBankAccountStatement(statementId, this.unitBankAccountId).subscribe(
      fileDownloaded => {
        this.isDownloading$.next(false);
        this.sendTelemetryEvent();
      },
      error => {
        this.isDownloading$.next(false);
        this.errorDownloading$.next(true);
      });
  }

  public getMonthName(monthNumber: number): string {
    return TerraUtils.monthNumberToMonthName(monthNumber);
  }

  /**
   * Creates the form
   */
  private generateForm(): void {
    this.statementForm = this.fb.group({
      year: [null, [Validators.required]],
      month: [{value: null, disabled: true}, [Validators.required]]
    });
  }

  /**
   * Inserts distinct years to availableYears
   */
  private setAvailableYears(): void {
    const distinctYears = new Set<string>(this.statements.map(statement => statement.year));
    this.availableYears$.next([...distinctYears]);
  }

  /**
   * Update availableMonths when year changes
   */
  private subscribeToYearChange() {
    this.statementForm.get('year').valueChanges.pipe(untilComponentDestroyed(this)).subscribe(year => {
      if (!!year) {
        const monthsAvailable = this.statements.filter(statement => statement.year === year);
        this.availableMonths$.next(monthsAvailable.map(statement => statement.month));
        this.statementForm.get('month').enable();
      } else {
        this.statementForm.get('month').disable();
        this.statementForm.get('month').setValue(null);
      }
    });
  }

  private sendTelemetryEvent() {
    this.userService.accountDetails$
      .pipe(take(1)).subscribe(userDetails => {
      this.telemetryService.create({
        eventID: '3001',
        eventTitle: userDetails.isAgent ? 'GP MONTHLY STATEMENT- DOWNLOAD' : 'LP MONTHLY STATEMENT- DOWNLOAD',
        sourcePage: document.location.href,
        ctNumber: userDetails.clientIdentifier
      }, AnalyticsServiceNameModel.Mixpanel);
    });
  }

}
